/**
 * THE PONS catalogue - price sheet v4.2, EUR, tax-exclusive.
 *
 * Transcribed from `pricing-specs/PONS_Pricing_Overview.html`, which is the
 * document the sales deck, the website and the invoice all come from. Every
 * surface that quotes a price, names a module, or says what a plan unlocks
 * reads it from here: the packages page, the plans page, the upgrade modal and
 * the lockout overlay. One catalogue, so a price cannot be right in one place
 * and stale in another.
 *
 * WHY IT LIVES IN THE FRONTEND. Prices are a published fact, not a secret, and
 * a pricing page that waits on a catalogue call is a pricing page you cannot
 * show a customer across a table.
 *
 * WHAT IS NEVER HERE: Stripe price ids, product ids, keys - anything that
 * authorises a charge. The client names a plan or a pack by CODE and the
 * backend resolves it to a price it owns. See `PackActions`.
 *
 * The `unlocks` on each row are the ENTITLEMENT keys from
 * `config/entitlementKeys`, so "what does this module buy me" and "what does
 * this gate check" are answered from the same vocabulary - which is the thing
 * that was wrong when an org on Full found Contacts locked.
 */

import { AiFeatureKey, SectionKey } from './entitlementKeys';

export type BillingPeriod = 'annual' | 'monthly';

/** Money is integer cents everywhere; a price is never a float. */
export const formatEur = (cents: number): string =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);

/* ------------------------------------------------------------------ */
/*  Meters                                                             */
/* ------------------------------------------------------------------ */

/**
 * The five things PONS counts.
 *
 * Allowances are PER SEAT, POOLED FIRM-WIDE, and reset on the first day of
 * each billing month; they do not carry over. Prepaid packs never expire and
 * sit outside the pool. Failed runs never count, and Ask/document Q&A is never
 * metered at all - both worth saying on any surface that shows a meter, because
 * a customer watching a number go down assumes everything moves it.
 */
export type MeterKey =
  | 'review_runs'
  | 'table_runs'
  | 'generations'
  | 'queries'
  | 'matter_analyses';

export const METER_LABELS: Record<MeterKey, string> = {
  review_runs: 'review runs',
  table_runs: 'table runs',
  generations: 'generations',
  queries: 'research queries',
  matter_analyses: 'matter analyses',
};

/** What one unit of each meter actually is, in the customer's words. */
export const METER_DEFINITIONS: Record<MeterKey, string> = {
  review_runs:
    'One document read against a playbook - a clause review, a compliance check, a repaper. A 300-page lease is still one run.',
  table_runs: 'One document through one batch extraction, across the columns you defined.',
  generations: 'One drafted document produced from your templates.',
  queries: 'One research question answered with citations over the curated corpus.',
  matter_analyses:
    'One matter opened and understood - baseline, questionnaire, suggested tasks, enrichment.',
};

export type Allowance = Partial<Record<MeterKey, number>>;

/* ------------------------------------------------------------------ */
/*  Plans                                                              */
/* ------------------------------------------------------------------ */

export interface PlanRow {
  code: string;
  name: string;
  /** Per seat, per month, in cents. Null = quoted. */
  annual: number | null;
  monthly: number | null;
  tagline: string;
  includes: string[];
  /** Per-seat monthly allowance this plan contributes to the firm pool. */
  allowance?: Allowance;
  /** Entitlement keys this plan grants - the same keys the gates check. */
  unlocks?: { sections?: SectionKey[]; aiFeatures?: AiFeatureKey[] };
  featured?: boolean;
  footnote?: string;
}

/** Sections every paid plan carries - the practice platform itself. */
const PLATFORM_SECTIONS: SectionKey[] = [
  'dashboard', 'matters', 'contacts', 'tasks', 'calendar', 'files',
  'projects', 'messages', 'notifications', 'people', 'departments', 'teams',
];

export const PLANS: PlanRow[] = [
  {
    code: 'starter',
    name: 'PONS Starter',
    annual: 7900,
    monthly: 9900,
    tagline: 'The practice platform. Add the AI you actually use.',
    includes: [
      'Matters, contacts and tasks',
      'Document management, 100 GB included',
      'Real-time collaboration, end-to-end encrypted',
      'Calendar, teams and dashboards',
      'AI by the pack from €29, or add single modules from €15',
    ],
    unlocks: { sections: [...PLATFORM_SECTIONS, 'finance'] },
  },
  {
    code: 'full',
    name: 'PONS Full',
    annual: 19900,
    monthly: 24900,
    tagline: 'Every module, every meter, priority support.',
    includes: [
      'Everything in Starter',
      'All 7 AI modules included',
      'Pooled monthly allowance across the firm',
      'Priority support',
      '100 GB storage',
    ],
    /* Per seat, per month, pooled. A ten-seat firm shares 700 reviews,
       6,000 extractions, 300 drafts, 3,000 answers and 120 matter analyses. */
    allowance: {
      review_runs: 70,
      table_runs: 600,
      generations: 30,
      queries: 300,
      matter_analyses: 12,
    },
    unlocks: {
      sections: [
        ...PLATFORM_SECTIONS, 'finance', 'reviews', 'tables', 'research',
        'data_sources', 'templates', 'analytics', 'matter_economics', 'drafting',
      ],
      aiFeatures: [
        'review_ai', 'tables_ai', 'drafts', 'research', 'insights',
        'template_ai', 'data_source_indexing', 'onboarding_ai',
      ],
    },
    featured: true,
    footnote: 'Saves 42% against buying the modules one by one.',
  },
  {
    code: 'enterprise',
    name: 'Enterprise',
    annual: null,
    monthly: null,
    tagline: 'Full, plus what larger firms ask of a vendor.',
    includes: [
      'Everything in Full, on every meter',
      'SSO and a custom DPA',
      '3 data sources and 30 templates',
      'Onboarding, invoicing and a named contact',
    ],
    footnote: 'From €199/seat/mo · 75+ seats · annual invoice only.',
  },
];

/**
 * Enterprise is banded by seat count, not quoted from nothing.
 *
 * These discount from the REGIONAL price, not from Tier 1 - a point the sheet
 * makes explicitly, and the reason no tier maths is applied to them here.
 */
export interface EnterpriseBand {
  seats: string;
  annual: number;
  includes: string;
}

export const ENTERPRISE_BANDS: EnterpriseBand[] = [
  { seats: '75–149 seats', annual: 18900, includes: 'Full + SSO, custom DPA, onboarding · 3 data sources / 30 templates · named contact' },
  { seats: '150–299 seats', annual: 17900, includes: 'The above · 5 data sources · structured onboarding · quarterly reviews' },
  { seats: '300+ seats', annual: 16900, includes: 'The above · 8 data sources · custom playbook development · SLA · negotiated pool' },
];

/* ------------------------------------------------------------------ */
/*  Modules - added to a Starter seat, all included in Full            */
/* ------------------------------------------------------------------ */

export interface ModuleRow {
  code: string;
  name: string;
  /** Per seat, per month, in cents. */
  annual: number;
  monthly: number;
  description: string;
  /** Per-seat monthly allowance this module adds to the pool. */
  allowance?: Allowance;
  /** Said in words where the module is not metered, or is metered oddly. */
  allowanceNote?: string;
  /** The entitlement keys it grants. */
  unlocks: { sections?: SectionKey[]; aiFeatures?: AiFeatureKey[] };
}

export const MODULES: ModuleRow[] = [
  {
    code: 'review',
    name: 'Review & repaper',
    annual: 5900, monthly: 7100,
    description: 'Playbooks, clause review, redlines, repaper.',
    allowance: { review_runs: 40 },
    /* `scan_ai` (Legislation Scan) is offered here: the backend lets
       `review_ai` open scans too, so Review is what unlocks it today. */
    unlocks: { sections: ['reviews'], aiFeatures: ['review_ai', 'scan_ai'] },
  },
  {
    code: 'tables',
    name: 'Tables',
    annual: 4500, monthly: 5500,
    description: 'Batch extraction, compliance mode, deep dives.',
    allowance: { table_runs: 500 },
    unlocks: { sections: ['tables'], aiFeatures: ['tables_ai'] },
  },
  {
    code: 'drafting',
    name: 'Drafting & strategies',
    annual: 4500, monthly: 5500,
    description: 'Template-grounded drafting.',
    allowance: { generations: 25 },
    /* Its OWN section - a top-level page, not a tab under Matters - and it
       carries `insights` with it: strategies and drafts are one product. */
    unlocks: { sections: ['drafting'], aiFeatures: ['drafts', 'insights'] },
  },
  {
    code: 'research',
    name: 'Legal research',
    annual: 4500, monthly: 5500,
    description: 'Cited answers over the curated legal corpus.',
    allowance: { queries: 150 },
    unlocks: { sections: ['research'], aiFeatures: ['research'] },
  },
  {
    code: 'knowledge',
    name: 'Knowledge',
    annual: 3500, monthly: 4500,
    description: 'The knowledge package for grounding AI in your own material.',
    allowanceNote: '10 templates (including playbooks) + 1 data source',
    /* Templates AND data sources: the allowance note says "10 templates + 1
       data source", so the module has to open both doors. */
    unlocks: {
      sections: ['templates', 'data_sources'],
      aiFeatures: ['template_ai', 'data_source_indexing'],
    },
  },
  {
    code: 'economics',
    name: 'Matter economics',
    annual: 1900, monthly: 2400,
    description: 'Billing rates, time entries, matter pricing, outcomes.',
    allowanceNote: 'Not metered',
    /* NOT `finance`. Starter already carries `finance` (invoices, payments);
       budgets, time entries and matter pricing are what this module sells. */
    unlocks: { sections: ['matter_economics'] },
  },
  {
    code: 'analytics',
    name: 'Analytics',
    annual: 1500, monthly: 1900,
    description: 'Firm-wide analytics and reporting across matters, people and work.',
    allowanceNote: 'Not metered',
    unlocks: { sections: ['analytics'] },
  },
];

export const moduleByCode = (code: string): ModuleRow | undefined =>
  MODULES.find((row) => row.code === code);

/**
 * Which module unlocks a given entitlement key.
 *
 * This is what lets a lock screen say "Review & repaper, €59/seat/month" for a
 * blocked `reviews` section instead of naming a catalogue row nobody has heard
 * of. Falls back to null so a caller can say nothing rather than guess.
 */
export const moduleForSection = (section: string): ModuleRow | null =>
  MODULES.find((m) => m.unlocks.sections?.includes(section as SectionKey)) || null;

export const moduleForAiFeature = (feature: string): ModuleRow | null =>
  MODULES.find((m) => m.unlocks.aiFeatures?.includes(feature as AiFeatureKey)) || null;

/* ------------------------------------------------------------------ */
/*  Flex packs - prepaid, never expire                                 */
/* ------------------------------------------------------------------ */

export interface PackRow {
  /** The code the backend resolves to a Stripe price. Never a price id. */
  code: string;
  name: string;
  price: number;
  units: number;
  unitLabel: string;
  meter: MeterKey;
  description: string;
  /** Effective unit price, as the sheet quotes it. */
  perUnit: string;
  featured?: boolean;
}

export const PACKS: PackRow[] = [
  {
    code: 'flex_review_20', name: 'Review runs', price: 2900, units: 20,
    unitLabel: 'review runs', meter: 'review_runs', perUnit: '€1.45 per review run',
    description: 'Twenty contract reviews, whenever you need them.',
  },
  {
    code: 'flex_table_250', name: 'Table runs', price: 2900, units: 250,
    unitLabel: 'table runs', meter: 'table_runs', perUnit: '€0.116 per table run',
    description: 'Two hundred and fifty documents through extraction.',
  },
  {
    code: 'flex_generation_15', name: 'Generations', price: 2900, units: 15,
    unitLabel: 'generations', meter: 'generations', perUnit: '€1.93 per generation',
    description: 'Fifteen drafts or documents, generated.',
  },
  {
    code: 'flex_research_60', name: 'Research queries', price: 2900, units: 60,
    unitLabel: 'queries', meter: 'queries', perUnit: '€0.48 per query',
    description: 'Sixty cited research answers.',
  },
  {
    code: 'flex_matter_10', name: 'Matter analyses', price: 8900, units: 10,
    unitLabel: 'matter analyses', meter: 'matter_analyses', perUnit: '€8.90 per matter',
    description: 'Ten full matter analyses - the heaviest operation on the platform.',
    featured: true,
  },
];

export const packByCode = (code: string): PackRow | undefined =>
  PACKS.find((pack) => pack.code === code);

/* ------------------------------------------------------------------ */
/*  Regional tiers                                                     */
/* ------------------------------------------------------------------ */

export interface RegionalTier {
  code: 'T1' | 'T2' | 'T3';
  discount: number;
  label: string;
  markets: string;
}

/**
 * SEATS AND INDUSTRY PACKS ONLY.
 *
 * The sheet is explicit: consumption packs and project packs are global list
 * everywhere, "because the compute behind them costs us the same wherever the
 * customer sits". A €29 flex pack is €29 in Sofia and in Stockholm, and
 * discounting one here would misquote a checkout the backend prices itself.
 *
 * 10% is the floor. Anything beyond Tier 3 is a negotiated exception, not a
 * published rate, so there is no Tier 4 to add.
 */
export const REGIONAL_TIERS: Record<string, RegionalTier> = {
  T1: { code: 'T1', discount: 0, label: 'Tier 1', markets: 'Nordics, DACH, Benelux, FR, IE, UK, CH, US, CA, AU, NZ, SG' },
  T2: { code: 'T2', discount: 0.05, label: 'Tier 2', markets: 'IT, ES, PT, GR, SI, CZ, EE, CY, MT, JP, KR, IL, AE' },
  T3: { code: 'T3', discount: 0.10, label: 'Tier 3', markets: 'PL, HU, SK, HR, LT, LV, RO, BG, RS, TR, UA' },
};

/** A SEAT price at a regional tier. Never call this on a pack. */
export const seatPriceAtTier = (cents: number, tier?: string | null): number => {
  const row = tier ? REGIONAL_TIERS[tier] : undefined;
  if (!row || !row.discount) return cents;
  return Math.round(cents * (1 - row.discount));
};

/** The saving of paying annually, as a whole percent. */
export const annualSavingPercent = (annual: number, monthly: number): number =>
  monthly > 0 ? Math.round(((monthly - annual) / monthly) * 100) : 0;

/* ------------------------------------------------------------------ */
/*  Trial                                                              */
/* ------------------------------------------------------------------ */

export const TRIAL = {
  days: 14,
  maxSeats: 10,
  description: 'Everything in Full, no card required.',
  allowance: {
    review_runs: 20,
    table_runs: 200,
    generations: 10,
    queries: 75,
    matter_analyses: 5,
  } as Allowance,
};

/** The allowance rules every meter surface should be able to state. */
export const ALLOWANCE_RULES = [
  'Pooled firm-wide and split evenly by default; the admin can reallocate.',
  'Resets on the first day of each billing month and does not carry over.',
  'Prepaid packs never expire and sit outside the monthly pool.',
  'Failed runs never count. Ask and document Q&A are never metered.',
];

/** "70 review runs · 600 table runs · …" for a plan or module row. */
export const describeAllowance = (allowance?: Allowance): string =>
  Object.entries(allowance || {})
    .map(([meter, count]) => `${count} ${METER_LABELS[meter as MeterKey]}`)
    .join(' · ');
